import { FormEvent, ReactNode } from 'react';

import clsx from 'clsx';

import { type FormSpacing, formSpacing } from '@/theme/size-tokens';

export type FormProps = {
  /** Form content - typically FormField, Fieldset, or Button components */
  children: ReactNode;
  /** Form submission handler - automatically prevents default */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  /** HTML form method */
  method?: 'get' | 'post';
  /** Form action URL for native submission */
  action?: string;
  /** Disable browser validation */
  noValidate?: boolean;
  /** HTML id attribute */
  id?: string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
  /** Spacing between form elements */
  spacing?: FormSpacing;
  /** Additional CSS classes applied to the form element */
  className?: string;
};

/**
 * Form - Semantic form wrapper with layout support
 *
 * Automatically prevents default on submit when onSubmit is provided.
 * Uses flexbox for layout with configurable direction, spacing, and alignment.
 *
 * @example
 * ```tsx
 * <Form onSubmit={handleSubmit} spacing="md">
 *   <FormField label="Email" required>
 *     <Input type="email" />
 *   </FormField>
 *   <Button type="submit">Submit</Button>
 * </Form>
 * ```
 */

function Form({
  children,
  onSubmit,
  method,
  action,
  noValidate = false,
  id,
  ariaLabel,
  spacing = 'md',
  className,
}: Readonly<FormProps>) {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    if (onSubmit) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      method={method}
      action={action}
      noValidate={noValidate}
      id={id}
      aria-label={ariaLabel}
      className={clsx('flex flex-col', formSpacing[spacing], className)}
    >
      {children}
    </form>
  );
}

Form.displayName = 'Form';

export default Form;
